/**
 * useLocation Hook
 * Manages GPS tracking, location permissions, and distance calculations for WSS
 * 
 * REUSED 100% from myDealer feature
 */

import { useState, useEffect, useCallback } from "react";
import {
  checkLocationPermission,
  calcDistance,
  openInGoogleMaps,
} from "../utils/gpsUtils";
import { DEFAULT_COORDINATES } from "../utils/wssConstants";

export const useLocation = () => {
  // Current GPS position
  const [currentLatitude, setCurrentLatitude] = useState(0);
  const [currentLongitude, setCurrentLongitude] = useState(0);
  const [currentAccuracy, setCurrentAccuracy] = useState(0);
  
  // Selected WSS location (null = no coordinates registered for this WSS)
  const [wssLatitude, setWssLatitude] = useState(null);
  const [wssLongitude, setWssLongitude] = useState(null);
  
  // Location error state
  const [locationError, setLocationError] = useState(false);

  /**
   * Check location permission
   */
  const checkPermission = useCallback(async () => {
    const hasPermission = await checkLocationPermission();
    setLocationError(!hasPermission);
  }, []);

  /**
   * Initialize GPS tracking with resilient error handling.
   * Only sets locationError on permission denied, NOT on transient GPS errors.
   */
  useEffect(() => {
    let watchId = null;
    let permissionStatus = null;

    const handleSuccess = (position) => {
      setCurrentLatitude(position.coords.latitude);
      setCurrentLongitude(position.coords.longitude);
      setCurrentAccuracy(position.coords.accuracy);
      // Only clear error if permission is not denied
      if (permissionStatus && permissionStatus.state !== "denied") {
        setLocationError(false);
      }
    };

    const handleError = (error) => {
      // Only set error if permission is denied, not on transient errors
      if (permissionStatus && permissionStatus.state === "denied") {
        setLocationError(true);
      }
    };

    const startTracking = () => {
      if ("geolocation" in navigator) {
        watchId = navigator.geolocation.watchPosition(
          handleSuccess,
          handleError,
          {
            enableHighAccuracy: true,
            maximumAge: 10000,
            timeout: 10000,
          }
        );
      } else {
        setLocationError(true);
      }
    };

    // Always attempt geolocation, but use Permissions API to set error only if denied
    if (navigator.permissions) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((status) => {
          permissionStatus = status;
          if (status.state === "denied") {
            setLocationError(true);
          } else {
            setLocationError(false);
            startTracking();
          }
          status.onchange = () => {
            if (status.state === "denied") {
              setLocationError(true);
              if (watchId) navigator.geolocation.clearWatch(watchId);
            } else {
              setLocationError(false);
              startTracking();
            }
          };
        })
        .catch(() => {
          // Permissions API not available or failed, just try geolocation
          startTracking();
        });
    } else {
      startTracking();
    }

    checkPermission();

    // Cleanup on unmount
    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
      if (permissionStatus && permissionStatus.onchange) {
        permissionStatus.onchange = null;
      }
    };
  }, [checkPermission]);

  /**
   * Set WSS location
   */
  const setWssLocation = useCallback((lat, lng) => {
    // Allow null to explicitly clear coordinates (e.g. WSS with no registered location)
    if (lat === null || lat === undefined) {
      setWssLatitude(null);
    } else if (!isNaN(lat)) {
      setWssLatitude(lat);
    }
    if (lng === null || lng === undefined) {
      setWssLongitude(null);
    } else if (!isNaN(lng)) {
      setWssLongitude(lng);
    }
  }, []);

  /**
   * Calculate distance between current position and WSS.
   * Returns Infinity when WSS has no registered coordinates.
   */
  const distanceToWss =
    wssLatitude !== null && wssLongitude !== null
      ? calcDistance(currentLatitude, currentLongitude, wssLatitude, wssLongitude)
      : Infinity;

  /**
   * Open WSS location in Google Maps
   */
  const openWssInMaps = useCallback(() => {
    openInGoogleMaps(wssLatitude, wssLongitude);
  }, [wssLatitude, wssLongitude]);

  /**
   * Open current location in Google Maps
   */
  const openCurrentLocationInMaps = useCallback(() => {
    openInGoogleMaps(currentLatitude, currentLongitude);
  }, [currentLatitude, currentLongitude]);

  return {
    // Current position
    currentLatitude,
    currentLongitude,
    currentAccuracy,
    
    // WSS location - use default coords for map display; raw null for no-coordinate detection
    wssLatitude: wssLatitude ?? DEFAULT_COORDINATES.latitude,
    wssLongitude: wssLongitude ?? DEFAULT_COORDINATES.longitude,
    wssHasCoordinates: wssLatitude !== null && wssLongitude !== null,
    setWssLocation,
    
    // Distance  
    distanceToWss,
    
    // Location error
    locationError,
    checkPermission,
    
    // Map functions
    openWssInMaps,
    openCurrentLocationInMaps,
  };
};

export default useLocation;
